import {
  Component,
  Input,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter
} from '@angular/core';
import { MAPBOX_ACCESS_TOKEN } from '@gh/config';
import mapboxgl, { Map as MapboxMap, Marker as MapboxMarker } from 'mapbox-gl';

@Component({
  standalone: false,
  selector: 'gh-mapbox-map',
  template: `
    <div class="map-container" [style.height]="height">
      <div class="map-skeleton" *ngIf="isLoading && !isUserDrag"></div>
      <div #mapContainer class="map" [class.map-loading]="isLoading && !isUserDrag"></div>
      <button 
        *ngIf="showReturnToMarker && markerCoordinates && !isLoading"
        class="return-to-marker-btn"
        (click)="returnToMarker()"
        type="button"
        title="Re-center on my location">
        <mat-icon svgIcon="gh:location-marker" class="s20"></mat-icon>
      </button>
    </div>
  `,
  styles: [`
    .map-container {
      width: 100%;
      position: relative;
      overflow: hidden;
      border: 1.5px solid #e0e0e0;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }

    .map {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }

    .map-loading {
      opacity: 0.3;
      pointer-events: none;
    }

    .map-skeleton {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200px 100%;
      animation: skeleton-loading 1.5s infinite;
      z-index: 1000;
      border-radius: 8px;
    }

    @keyframes skeleton-loading {
      0% {
        background-position: -200px 0;
      }
      100% {
        background-position: calc(200px + 100%) 0;
      }
    }

    .return-to-marker-btn {
      position: absolute;
      bottom: 12px;
      left: 12px;
      width: 40px;
      height: 40px;
      background: white;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      transition: all 0.2s ease;
      color: #333;
      padding: 0;
    }

    .return-to-marker-btn:hover {
      background: #f5f5f5;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      transform: translateY(-1px);
    }

    .return-to-marker-btn:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    }

    .return-to-marker-btn mat-icon {
      width: 20px;
      height: 20px;
    }
  `]
})
export class MapboxMapComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() coordinates: [number, number] | null = null;
  @Input() isUserDrag: boolean = false;
  @Input() zoom = 14;
  @Input() height: string = '400px';
  @Output() coordinatesChanged = new EventEmitter<[number, number]>();
  @Output() load = new EventEmitter<void>();

  @ViewChild('mapContainer', { static: false }) mapContainerRef!: ElementRef;

  isLoading = true;
  showReturnToMarker = false;
  markerCoordinates: [number, number] | null = null;
  private map: MapboxMap | null = null;
  private marker: MapboxMarker | null = null;
  private checkMarkerVisibilityHandler = () => this.checkMarkerVisibility();
  private markerZoomLevel: number | null = null;
  private readonly boundWindowResize = (): void => {
    this.onWindowResize();
  };

  constructor() {
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN ?? '';
  }

  ngAfterViewInit(): void {
    this.initializeMap();
    window.addEventListener('resize', this.boundWindowResize);
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.off('moveend', this.checkMarkerVisibilityHandler);
      this.map.off('zoomend', this.checkMarkerVisibilityHandler);
      this.map.remove();
    }
    window.removeEventListener('resize', this.boundWindowResize);
  }

  private onWindowResize(): void {
    if (this.map && this.mapContainerRef?.nativeElement) {
      try {
        const container = this.mapContainerRef.nativeElement;
        if (container.offsetWidth > 0 && container.offsetHeight > 0) {
          setTimeout(() => {
            if (this.map && this.mapContainerRef?.nativeElement) {
              try {
                this.map.resize();
              } catch (error) {
                console.warn('Map resize error:', error);
              }
            }
          }, 100);
        }
      } catch (error) {
        console.warn('Map container check error:', error);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['coordinates']?.currentValue && this.map) {
      this.updateMarker(changes['coordinates'].currentValue);
    }
    if (changes['height'] && this.map && this.mapContainerRef?.nativeElement) {
      setTimeout(() => {
        if (this.map && this.mapContainerRef?.nativeElement) {
          try {
            const container = this.mapContainerRef.nativeElement;
            if (container.offsetWidth > 0 && container.offsetHeight > 0) {
              this.map.resize();
            }
          } catch (error) {
            console.warn('Map resize error on height change:', error);
          }
        }
      }, 100);
    }
  }

  private initializeMap(): void {
    const container = this.mapContainerRef.nativeElement;
    this.isLoading = true;

    this.map = new mapboxgl.Map({
      container,
      style: 'mapbox://styles/mapbox/standard',
      center: this.coordinates || [-95.7129, 37.0902],
      zoom: this.zoom,
    });

    this.map.on('load', () => {
      setTimeout(() => {
        if (this.map && this.mapContainerRef?.nativeElement) {
          try {
            const container = this.mapContainerRef.nativeElement;
            if (container.offsetWidth > 0 && container.offsetHeight > 0) {
              this.map.resize();
            }
          } catch (error) {
            console.warn('Map resize error on load:', error);
          }
        }
        if (this.coordinates) {
          this.addOrUpdateMarker(this.coordinates);
        }
        this.setupMapEventListeners();
        this.isLoading = false;
        this.load.emit();
      }, 100);
    });
  }

  private addOrUpdateMarker(coordinates: [number, number]): void {
    this.markerCoordinates = coordinates;
    
    if (!this.marker) {
      this.marker = new mapboxgl.Marker({ draggable: true })
        .setLngLat(coordinates)
        .addTo(this.map!);

      this.marker.on('dragend', () => {
        const { lng, lat } = this.marker!.getLngLat();
        this.markerCoordinates = [lng, lat];
        this.markerZoomLevel = this.map!.getZoom();
        this.coordinatesChanged.emit([lng, lat]);
        this.checkMarkerVisibility();
      });
    } else {
      this.marker.setLngLat(coordinates);
    }
    
    if (this.map) {
      this.markerZoomLevel = this.map.getZoom();
    }
    
    setTimeout(() => this.checkMarkerVisibility(), 100);
  }

  public updateMarker(coordinates: [number, number]): void {
    this.coordinates = coordinates;
    this.isLoading = true;

    this.map?.easeTo({
      center: coordinates,
      zoom: this.zoom,
    });

    this.markerZoomLevel = this.zoom;

    this.addOrUpdateMarker(coordinates);
    setTimeout(() => {
      if (this.map && this.mapContainerRef?.nativeElement) {
        try {
          const container = this.mapContainerRef.nativeElement;
          if (container.offsetWidth > 0 && container.offsetHeight > 0) {
            this.map.resize();
          }
        } catch (error) {
          console.warn('Map resize error on marker update:', error);
        }
      }
      this.isLoading = false;
      this.load.emit();
      this.checkMarkerVisibility();
    }, 500);
  }

  private setupMapEventListeners(): void {
    if (!this.map) return;

    this.map.on('moveend', this.checkMarkerVisibilityHandler);
    this.map.on('zoomend', this.checkMarkerVisibilityHandler);
    
    setTimeout(() => this.checkMarkerVisibility(), 200);
  }

  private checkMarkerVisibility(): void {
    if (!this.map || !this.markerCoordinates || !this.marker) {
      this.showReturnToMarker = false;
      return;
    }

    const bounds = this.map.getBounds();
    if (!bounds) {
      this.showReturnToMarker = false;
      return;
    }

    const mapCenter = this.map.getCenter();
    const currentZoom = this.map.getZoom();

    const isVisible = bounds.contains(this.markerCoordinates as [number, number]);
    
    if (!isVisible) {
      this.showReturnToMarker = true;
      return;
    }

    const markerLng = this.markerCoordinates[0];
    const markerLat = this.markerCoordinates[1];
    const centerLng = mapCenter.lng;
    const centerLat = mapCenter.lat;

    const deltaLng = Math.abs(centerLng - markerLng);
    const deltaLat = Math.abs(centerLat - markerLat);

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const viewportWidth = ne.lng - sw.lng;
    const viewportHeight = ne.lat - sw.lat;

    const thresholdLng = viewportWidth * 0.25;
    const thresholdLat = viewportHeight * 0.25;
    const isOffCenter = deltaLng > thresholdLng || deltaLat > thresholdLat;

    const zoomChanged = this.markerZoomLevel !== null && 
                        Math.abs(currentZoom - this.markerZoomLevel) > 0.5;

    this.showReturnToMarker = isOffCenter || zoomChanged;
  }

  public returnToMarker(): void {
    if (!this.map || !this.markerCoordinates) return;

    this.map.flyTo({
      center: this.markerCoordinates,
      zoom: this.zoom,
      duration: 1000
    });

    this.markerZoomLevel = this.zoom;

    setTimeout(() => {
      this.checkMarkerVisibility();
    }, 1100);
  }
}
